<template>
  <span class="check-status" :class="`check-status--${variant}`">
    <span v-if="variant === 'icon'" class="check-status-icon" :class="`check-status-icon--${result.key}`" aria-hidden="true">
      <ph-icon v-if="result.key === 'passed'" name="check" :size="10" />
      <span v-else-if="result.key !== 'tip'" class="check-status-dot" />
    </span>
    <span v-if="variant === 'meter'" class="check-status-meter" aria-hidden="true">
      <span
        v-for="segment in 4"
        :key="segment"
        class="check-status-segment"
        :class="{ [`check-status-segment--${result.key}`]: segment <= result.segments }"
      />
    </span>
    <span v-if="variant !== 'icon'" class="check-status-result">
      <span class="check-status-label" :class="`check-status-label--${result.key}`">
        {{ result.label }}<span class="visually-hidden">{{ exactValue }}</span>
      </span>
      <span v-if="scored" class="check-status-score" aria-hidden="true">{{ score }}/10</span>
    </span>
  </span>
</template>

<script>
import PhIcon from './PhIcon.vue'
import { formatScore, getCheckResult, hasCheckScore } from '../score/report.mjs'

export default {
  name: 'CheckStatus',
  components: { PhIcon },
  props: {
    check: { type: Object, required: true },
    // icon: the status circle before a check; meter: four segments and the label; label: the label alone.
    variant: { type: String, default: 'label', validator: (value) => ['icon', 'meter', 'label'].includes(value) }
  },
  computed: {
    result () {
      return getCheckResult(this.check)
    },
    scored () {
      return hasCheckScore(this.check)
    },
    score () {
      return formatScore(this.check.score)
    },
    exactValue () {
      return this.scored ? `, ${this.score} out of 10` : ', not scored'
    }
  }
}
</script>

<style scoped>
.check-status {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.check-status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: var(--ex-radius-pill);
  flex: none;
}

.check-status-icon--passed {
  background: var(--ex-check-passed-bg);
  color: var(--ex-check-passed);
}

.check-status-icon--almost,
.check-status-icon--needs-work,
.check-status-icon--failed {
  border: 2px solid currentColor;
}

.check-status-icon--almost {
  color: var(--ex-score-good);
}

.check-status-icon--needs-work {
  color: var(--ex-score-mid);
}

.check-status-icon--failed {
  color: var(--ex-score-low);
}

.check-status-icon--tip {
  border: 1.5px dashed var(--ex-color-neutral-100);
}

.check-status-dot {
  width: 5px;
  height: 5px;
  border-radius: var(--ex-radius-pill);
  background: currentColor;
}

.check-status-meter {
  display: inline-flex;
  gap: 3px;
}

.check-status-segment {
  width: 14px;
  height: 6px;
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-hairline);
}

.check-status-segment--passed,
.check-status-segment--almost {
  background: var(--ex-score-good);
}

.check-status-segment--needs-work {
  background: var(--ex-score-mid);
}

.check-status-segment--failed {
  background: var(--ex-score-low);
}

.check-status-result {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.check-status--meter {
  align-items: flex-start;
}

.check-status--meter .check-status-meter {
  margin-top: 6px;
}

.check-status--meter .check-status-result {
  width: 84px;
  align-items: flex-start;
}

.check-status-score {
  font-size: 12px;
  color: var(--ex-color-neutral-500);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.check-status-label {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.check-status--meter .check-status-label {
  font-size: 13px;
}

.check-status-label--passed {
  color: var(--ex-check-passed);
}

.check-status-label--almost {
  color: var(--ex-check-almost);
}

.check-status-label--needs-work {
  color: var(--ex-check-needs-work);
}

.check-status-label--failed {
  color: var(--ex-check-failed);
}

.check-status-label--tip {
  font-weight: 400;
  color: var(--ex-color-neutral-500);
}
</style>
